import { Consent } from "../../../dataTypes/type-consent";
import { DataService } from "../data.service";
import { UtilityService } from "../../utility.service";
import { ConsentsStatusService } from "../../consents-services/consents-status.service";
import { GetConsentsService } from "../../consents-services/get-consents.service";
import UserCentricViewController from "../../../ui-components/usercentrics-button/usercentrics-view-controller";
import { ConsentsToggleService } from "../../consents-services/consents-toggle-detail.service";

export class PrivacySettings {

    private embedConfigs: Object = {};
    private readonly cornerModalSettingsHtml = require('../../../ui-components/usercentrics-button/privacy-views/secondary-advance-privacy-view/settings/templates/privacy-corner-modal-settings-templates/corner-settings-template-1.hbs');
    private renderFunction = (id?: string) => { };


    private copiedText: string = '';

    /**
     * The constructor requires a render function to be passed
     * @param renderFunction 
     */
    public constructor(renderFunction: (string?) => void) {
        this.renderFunction = renderFunction;
    }

    /**
     * Embedds the view into the passed HTMLElement with all IDs prefixed
     * @param embedTag 
     * @param idPrefix 
     * @param utilityService 
     */
    public embed(embedTag: HTMLElement, idPrefix: string, utilityService: UtilityService) {
        const tagId = embedTag.id;
        let isNewEmbed = false;
        // Initialize the config that is used to pass interaction information
        if (!this.embedConfigs.hasOwnProperty(tagId)) {
            this.embedConfigs[tagId] = {
                consentToggleFields: {}
            };
            isNewEmbed = true;
        }

        const copyFields = {};

        // Duplicate the categories and consents so that changes to the object won't affect global scope
        const categories: Consent.Category[] = DataService.categories;
        // copy information for categorires and consents
        categories.forEach((category) => {
            category.consents.forEach((consent) => {
                if (isNewEmbed) {
                    this.embedConfigs[tagId].consentToggleFields[consent.templateId] = {
                        showDetail: false,
                        showSubConsents: false,
                        showHistory: false
                    };
                }

                copyFields[consent.templateId] = {
                    copiedConsentId: (this.copiedText === GetConsentsService.getConsentId(consent)),
                    copiedControllerId: (this.copiedText === consent.controllerId),
                    copiedProcessorId: (this.copiedText === consent.processorId)
                };
            });
        });

        const optinConsent = GetConsentsService.getOptinConsent();

        const viewDataSettings = {
            showCategoryDescriptions: true,
            allDisabled: !utilityService.isConsentChangeAllow,
            settings: utilityService.settingsService.settings,
            categories: categories,
            consentCopyFields: copyFields,
            consentToggleFields: this.embedConfigs[tagId].consentToggleFields,
            categoryToggleFields: this.embedConfigs[tagId],
            consentTemplates: DataService.consentTemplates,
            allStatuses: ConsentsStatusService.selectAllStatus.status,
            allStatusesIntermediate: ConsentsStatusService.selectAllStatus.isIntermediateState,
            showHideBanner: utilityService.settingsService.elementsVisibility.bannerVisible,
            optInConsent: { isHidden: true },
            bannerMessage: optinConsent ? optinConsent.description : '',
            icons: UserCentricViewController.icons,
            idPrefix: idPrefix
        };

        embedTag.innerHTML = '<div class="usercentrics-button layout-1"><div class="uc-corner-modal">'
            + '<div class="uc-corner-modal-content">'
            + this.cornerModalSettingsHtml(viewDataSettings)
            + '</div>'
            + '</div></div>';

        // Assign event listeners
        window.setTimeout(this.addEventListener.bind(this, tagId, categories, utilityService));
    }

    /**
     * 
     * @param tag 
     * @param categories 
     */
    private addEventListener(id: string, categories: Consent.Category[], utilityService: UtilityService) {

        const tag = document.getElementById(id);
        if (!tag) {
            throw "Embedding with id '" + id + "' not found";
        }

        // Selet all
        const selectAllElement = tag.querySelector('.uc-select-all-items');
        if (selectAllElement) {
            selectAllElement.addEventListener('click', (event) => {
                const newSelectAllState = !ConsentsStatusService.selectAllStatus.status;
                DataService.consents.forEach((consent) => {
                    ConsentsStatusService.setUnsavedConsentStatus(consent, newSelectAllState);
                });

                const action = 'onToggleSelectAll';
                const updatedBy = 'explicit';
                utilityService.saveConsentsService.saveConsents(action, updatedBy);

                // Render all embeddings
                this.renderFunction();
            })
        }

        // Bind open/close eventlistener on category level
        categories.forEach((category) => {
            const categoryElement: HTMLElement = tag.querySelector(`#${id}uc-category-click-${category.categorySlug}`);
            if (categoryElement) {
                categoryElement.onclick = (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    if (!this.embedConfigs[id].hasOwnProperty(category.categorySlug)) {
                        this.embedConfigs[id][category.categorySlug] = false;
                    }

                    // Save the state of the category in config
                    this.embedConfigs[id][category.categorySlug] = !this.embedConfigs[id][category.categorySlug];

                    // Re-render embeddings
                    this.renderFunction(id);
                };
            }

            // Checkbox for toggling status
            const categoryToggleElement = tag.querySelector('.uc-form-check-input[data-event="toggle-category"][data-id="' + category.categorySlug + '"]');
            if (categoryToggleElement) {

                // Stop click event to prevent category open/close
                categoryToggleElement.addEventListener('click', (event) => {
                    event.stopPropagation();
                });
                categoryToggleElement.addEventListener('change', (event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    // We need to fetch the real category
                    const liveCategory = GetConsentsService.getAppropriateCategory(DataService.categories, category.categorySlug);

                    ConsentsStatusService.changeCategorySelectedStatus(liveCategory, (<HTMLInputElement>event.target).checked);

                    const action = 'onToggleConsent';
                    const updatedBy = 'explicit';
                    utilityService.saveConsentsService.saveConsents(action, updatedBy);

                    // Re-render embeddings
                    this.renderFunction();
                });
            }

            // Bind open history and details
            category.consents.forEach(consent => {

                const showSubConsentsElements = tag.querySelectorAll('.uc-sub-consent-id-' + consent.templateId);
                if (showSubConsentsElements) {
                    Array.prototype.forEach.call(showSubConsentsElements, showSubConsentElement => {
                        if (showSubConsentElement) {
                            showSubConsentElement.addEventListener('click', (event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                ConsentsToggleService.changeConsentShowSubConsentStatus(this.embedConfigs[id].consentToggleFields[consent.templateId], !this.embedConfigs[id].consentToggleFields[consent.templateId].showSubConsents);
                                this.renderFunction(id);
                            });
                        }
                    });
                }

                const consentElementHistory = tag.querySelector('.uc-concent-icons-div[data-event="open-consent-history"][data-id="' + consent.templateId + '"]');
                if (consentElementHistory) {
                    consentElementHistory.addEventListener('click', (event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        // Save the state of the consent in config
                        ConsentsToggleService.changeConsentShowHistoryStatus(this.embedConfigs[id].consentToggleFields[consent.templateId], !this.embedConfigs[id].consentToggleFields[consent.templateId].showHistory);

                        this.renderFunction(id);
                    });
                }

                // For opening details
                const consentElementDetails = tag.querySelector('.uc-concent-icons-div[data-event="open-consent-details"][data-id="' + consent.templateId + '"]');
                if (consentElementDetails) {
                    consentElementDetails.addEventListener('click', (event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        // Save the state of the consent in config
                        ConsentsToggleService.changeConsentShowDetailStatus(this.embedConfigs[id].consentToggleFields[consent.templateId], !this.embedConfigs[id].consentToggleFields[consent.templateId].showDetail);

                        this.renderFunction(id)
                    });
                }

                // For toggeling consent
                const consentElementStatus = tag.querySelector('.uc-form-check-input[data-event="toggle-consent"][data-id="' + consent.templateId + '"]');
                if (consentElementStatus) {
                    consentElementStatus.addEventListener('change', (event) => {
                        event.preventDefault();
                        event.stopPropagation();

                        ConsentsStatusService.setUnsavedConsentStatus(consent, !consent.unsavedConsentStatus);
                        const action = 'onToggleConsent';
                        const updatedBy = 'explicit';
                        utilityService.saveConsentsService.saveConsents(action, updatedBy);

                        this.renderFunction();
                    });
                }

                // For copying fields
                const copyElements = tag.getElementsByClassName('consent-copy-' + consent.templateId);

                Array.prototype.forEach.call(copyElements, copyElement => {
                    copyElement.addEventListener('click', event => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (copyElement.getAttribute('data-type') === 'consentId') {
                            this.copyText(GetConsentsService.getConsentId(consent), id);
                        } else {
                            this.copyText(consent[copyElement.getAttribute('data-type')], id);
                        }
                    });
                });

            });
        });


        // Bind open info modal
        const openConsentInfoModalBtns = tag.querySelectorAll(
            '[data-event="open-consent-info-modal"]'
        );
        Array.prototype.forEach.call(openConsentInfoModalBtns, openConsentInfoModalBtn => {

            openConsentInfoModalBtn.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();

                if (openConsentInfoModalBtn.getAttribute('data-id')) {
                    utilityService.UserCentricViewController.openConsentInfoModal(
                        openConsentInfoModalBtn.getAttribute('data-id')
                    );
                } else {
                    utilityService.UserCentricViewController.openConsentInfoModal(id);
                }
            });
        });
    }

    private copyText(string, id) {
        const el = document.createElement('textarea');
        el.value = string;
        document.body.appendChild(el);
        el.select();
        try {
            document.execCommand('copy');
            document.body.removeChild(el);
            this.copiedText = string;
            this.renderFunction(id);
        } catch (err) {
            console.log(err);
            document.body.removeChild(el);
        }
    }
}